<section class="dashboard-overview" aria-labelledby="dashboard-title">
  <header class="dashboard-overview__header">
    <h1 id="dashboard-title" class="page-title fs-22 fw-700 m-0">Access Management Dashboard</h1>
  </header>

  @if (isLoading()) {
  <div class="dashboard-overview__status" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="40" />
    <p>Loading dashboard…</p>
  </div>
  } @else if (loadError()) {
  <div class="dashboard-overview__status dashboard-overview__status--error" role="alert">
    <app-icon name="warning" color="warn" size="md" ariaLabel="Error" />
    <p>{{ loadError() }}</p>
    <button type="button" class="primary-button" (click)="retry()">Try again</button>
  </div>
  } @else {
  <ul class="stat-grid" aria-label="Queue summary">
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">Unreviewed</span>
        <span class="badge badge--pending-review badge--compact">System</span>
      </span>
      <span class="stat-card__value">{{ unreviewedCount() }}</span>
      <span class="stat-card__sub">No staff activity yet</span>
    </li>
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">In Progress</span>
        <span class="badge badge--in-progress badge--compact">Staff</span>
      </span>
      <span class="stat-card__value">{{ inProgressCount() }}</span>
      <span class="stat-card__sub">Currently under review</span>
    </li>
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">Needs Info</span>
        <span class="badge badge--needs-info badge--compact">Staff</span>
      </span>
      <span class="stat-card__value stat-card__value--warn">{{ needsInfoCount() }}</span>
      <span class="stat-card__sub">Awaiting patient response</span>
    </li>
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">Caregiver / Proxy Verifications</span>
      </span>
      <span class="stat-card__value">{{ proxyVerificationPendingCount() }}</span>
      <span class="stat-card__sub">Pending approval</span>
    </li>
  </ul>

  <ul class="stat-grid stat-grid--wide" aria-label="Workload summary">
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">Overdue</span>
        <span class="badge badge--overdue badge--compact">System</span>
      </span>
      <span class="stat-card__value stat-card__value--error">{{ overdueCount() }}</span>
      <span class="stat-card__sub">7+ days old, no resolution</span>
    </li>
    <li class="stat-card">
      <span class="stat-card__label-row">
        <span class="stat-card__label">Assigned to Me</span>
      </span>
      <span class="stat-card__value">{{ assignedToMeCount() }}</span>
      <span class="stat-card__sub">Items currently assigned</span>
    </li>
  </ul>

  <section class="recent-card" aria-labelledby="recent-heading">
    <header class="recent-card__header flex align-center gap-12">
      <h2 id="recent-heading" class="recent-card__title m-0">Recent New Requests</h2>
      <button
        type="button"
        class="primary-button recent-card__queue-button"
        (click)="viewFullQueue()"
      >
        <app-icon
          name="open_in_full"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        />
        View Full Queue
      </button>
    </header>

    @if (recentRequests().length === 0) {
    <div
      class="table-status flex flex-col align-center justify-center text-center"
      aria-live="polite"
    >
      <h3>No requests</h3>
      <p>There are currently no linking requests to review.</p>
    </div>
    } @else {
    <div class="queue-table queue-table--linking" role="table" aria-label="Recent new requests">
      <div class="queue-table__header queue-table__grid gap-12 fw-600 uppercase" role="row">
        <div role="columnheader">Submitted</div>
        <div role="columnheader">Patient Name</div>
        <div role="columnheader">DOB</div>
        <div role="columnheader">Identifier</div>
        <div role="columnheader">Assigned To</div>
        <div role="columnheader">Status</div>
      </div>

      <div class="queue-table__body" role="rowgroup">
        @for (request of paginatedRequests(); track request.id) {
        <div
          class="queue-table__row queue-table__grid align-center gap-12 cursor"
          role="row"
          tabindex="0"
          [attr.aria-label]="request.ariaLabel"
          (click)="openRequest(request.id)"
          (keydown)="onRowKeydown($event, request.id)"
        >
          <div class="cell cell--secondary" role="cell">{{ getRelativeSubmittedAt(request) }}</div>
          <div class="cell cell--primary fw-600" role="cell">{{ request.patientName }}</div>
          <div class="cell cell--primary" role="cell">{{ request.dob }}</div>
          <div
            class="cell cell--primary"
            role="cell"
            [attr.aria-label]="request.identifierAriaLabel"
          >
            {{ request.identifier }}
          </div>
          <div class="cell" role="cell">
            <span class="assign-tag" [class.assign-tag--assigned]="request.assignedToDisplayName">
              <app-icon
                name="account_circle"
                size="xs"
                color="custom"
                customColor="currentColor"
                role="presentation"
              />
              {{ request.assignedToDisplayName ?? 'Unassigned' }}
            </span>
          </div>
          <div class="cell" role="cell">
            <span class="badge" [class]="'badge--' + request.statusKind"
              >{{ request.statusLabel }}</span
            >
          </div>
        </div>
        }
      </div>
    </div>

    <footer class="pagination flex align-center justify-between gap-12" aria-label="Pagination">
      <p class="pagination__summary m-0 fs-12">{{ showingSummary() }}</p>

      <nav class="pagination__controls flex flex-wrap justify-end gap-4" aria-label="Page controls">
        <button
          type="button"
          class="page-button cursor"
          (click)="goToPage(currentPage() - 1)"
          [disabled]="currentPage() === 1"
          aria-label="Previous page"
        >
          <app-icon
            name="chevron_left"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          />
        </button>

        @for (pageItem of pageItems(); track $index) { @if (pageItem === 'ellipsis') {
        <span class="pagination__ellipsis fs-12" aria-hidden="true">…</span>
        } @else {
        <button
          type="button"
          class="page-button fs-12 fw-600 cursor"
          [class.page-button--active]="pageItem === currentPage()"
          [attr.aria-current]="pageItem === currentPage() ? 'page' : null"
          (click)="goToPage(pageItem)"
        >
          {{ pageItem }}
        </button>
        } }

        <button
          type="button"
          class="page-button cursor"
          (click)="goToPage(currentPage() + 1)"
          [disabled]="currentPage() === totalPages()"
          aria-label="Next page"
        >
          <app-icon
            name="chevron_right"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          />
        </button>
      </nav>
    </footer>
    }
  </section>

  <section class="recent-card" aria-labelledby="recent-proxy-heading">
    <header class="recent-card__header flex align-center gap-12">
      <h2 id="recent-proxy-heading" class="recent-card__title m-0">
        Recent Caregiver / Proxy Verifications
      </h2>
      <button
        type="button"
        class="primary-button recent-card__queue-button recent-card__queue-button--proxy"
        (click)="viewFullProxyQueue()"
      >
        <app-icon
          name="open_in_full"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        />
        View Full Queue
      </button>
    </header>

    @if (recentProxyRequests().length === 0) {
    <div
      class="table-status flex flex-col align-center justify-center text-center"
      aria-live="polite"
    >
      <h3>No verifications</h3>
      <p>There are currently no caregiver or proxy verifications to review.</p>
    </div>
    } @else {
    <div
      class="queue-table queue-table--proxy"
      role="table"
      aria-label="Recent caregiver and proxy verifications"
    >
      <div class="queue-table__header queue-table__grid gap-12 fw-600 uppercase" role="row">
        <div role="columnheader">Submitted</div>
        <div role="columnheader">Patient Name</div>
        <div role="columnheader">Caregiver Name</div>
        <div role="columnheader">Relationship</div>
        <div role="columnheader">Assigned To</div>
        <div role="columnheader">Status</div>
      </div>

      <div class="queue-table__body" role="rowgroup">
        @for (request of paginatedProxyRequests(); track request.id) {
        <div
          class="queue-table__row queue-table__grid align-center gap-12 cursor"
          role="row"
          tabindex="0"
          [attr.aria-label]="request.ariaLabel"
          (click)="openProxyRequest(request.id)"
          (keydown)="onProxyRowKeydown($event, request.id)"
        >
          <div class="cell cell--secondary" role="cell">{{ getRelativeSubmittedAt(request) }}</div>
          <div class="cell cell--primary fw-600" role="cell">{{ request.patientName }}</div>
          <div class="cell cell--primary" role="cell">{{ request.caregiverName }}</div>
          <div class="cell cell--primary" role="cell">{{ request.relationship }}</div>
          <div class="cell" role="cell">
            <span class="assign-tag" [class.assign-tag--assigned]="request.assignedToDisplayName">
              <app-icon
                name="account_circle"
                size="xs"
                color="custom"
                customColor="currentColor"
                role="presentation"
              />
              {{ request.assignedToDisplayName ?? 'Unassigned' }}
            </span>
          </div>
          <div class="cell" role="cell">
            <span class="badge" [class]="'badge--' + request.statusKind"
              >{{ request.statusLabel }}</span
            >
          </div>
        </div>
        }
      </div>
    </div>

    <footer
      class="pagination flex align-center justify-between gap-12"
      aria-label="Verification pagination"
    >
      <p class="pagination__summary m-0 fs-12">{{ proxyShowingSummary() }}</p>

      <nav
        class="pagination__controls flex flex-wrap justify-end gap-4"
        aria-label="Verification page controls"
      >
        <button
          type="button"
          class="page-button cursor"
          (click)="goToProxyPage(currentProxyPage() - 1)"
          [disabled]="currentProxyPage() === 1"
          aria-label="Previous verifications page"
        >
          <app-icon
            name="chevron_left"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          />
        </button>

        @for (pageItem of proxyPageItems(); track $index) { @if (pageItem === 'ellipsis') {
        <span class="pagination__ellipsis fs-12" aria-hidden="true">…</span>
        } @else {
        <button
          type="button"
          class="page-button fs-12 fw-600 cursor"
          [class.page-button--active]="pageItem === currentProxyPage()"
          [attr.aria-current]="pageItem === currentProxyPage() ? 'page' : null"
          (click)="goToProxyPage(pageItem)"
        >
          {{ pageItem }}
        </button>
        } }

        <button
          type="button"
          class="page-button cursor"
          (click)="goToProxyPage(currentProxyPage() + 1)"
          [disabled]="currentProxyPage() === proxyTotalPages()"
          aria-label="Next verifications page"
        >
          <app-icon
            name="chevron_right"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          />
        </button>
      </nav>
    </footer>
    }
  </section>
  }
</section>
